'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'

type RevealProps = {
  children: ReactNode
  /** Delay in ms before the in-view class is applied (for staggered grids). */
  delay?: number
  /** Tailwind classes for the wrapper div (layout helpers, etc). */
  className?: string
  /** 'slideup' = 20% slide-up over 1s ease (Asana 004); default = subtle 12px/0.6s. */
  variant?: 'default' | 'slideup'
}

/**
 * Wrap a block of content to fade + translate up when it enters the viewport.
 * Uses native IntersectionObserver (zero dependency). Respects
 * `prefers-reduced-motion`: opted-out users see content fully visible
 * immediately. Triggers once per mount.
 *
 * Defensive guarantees against content getting stuck invisible:
 * - If the element is already within (or above) the viewport at mount time,
 *   it's shown synchronously instead of waiting on the observer's async
 *   callback (covers full-page/headless captures where no real incremental
 *   scroll ever happens, so the observer's "entered viewport" event never
 *   fires).
 * - A short fallback timer forces visibility if the observer hasn't fired
 *   by then, regardless of the reason (slow JS, reduced-motion edge cases,
 *   capture tooling quirks).
 */
export function Reveal({ children, delay = 0, className = '', variant = 'default' }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setShown(true)
      return
    }

    // Already visible (or scrolled past) at mount — don't wait on the async
    // observer callback, which may never come from a non-scrolling capture.
    // The slideup variant deliberately SKIPS this shortcut: like sal.js it
    // plays its entrance even for blocks already in view on load (the IO fires
    // immediately at mount, so the transition runs from the hidden state).
    if (variant !== 'slideup') {
      const rect = el.getBoundingClientRect()
      if (rect.top < window.innerHeight) {
        setShown(true)
        return
      }
    }

    let delayTimer: number | undefined
    // Never leave content stuck invisible — but a blanket post-load timer was
    // revealing every below-fold section ~1s after PAGE LOAD, long before the
    // visitor scrolled to them, so no scroll animation was ever seen (first
    // caught on the slideup cards, then again on the section reveals, Steve
    // 2026-08-19). BOTH variants now poll instead: force-show only if the
    // element is actually within the viewport yet the observer still hasn't
    // fired (capture tooling, IO failure) — off-screen content stays hidden
    // until it really enters.
    const fallbackTimer: number = window.setInterval(() => {
      const r = el.getBoundingClientRect()
      if (r.top < window.innerHeight * 0.8 && r.bottom > 0) {
        setShown(true)
        window.clearInterval(fallbackTimer)
      }
    }, 1500)

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        window.clearTimeout(fallbackTimer)
        window.clearInterval(fallbackTimer)
        if (delay > 0) {
          delayTimer = window.setTimeout(() => setShown(true), delay)
          io.disconnect()
          return
        }
        setShown(true)
        io.disconnect()
      },
      variant === 'slideup'
        ? // sal.js/artifact recipe — fire when 20% of the card is visible
          { threshold: 0.2 }
        : { threshold: 0.15, rootMargin: '0px 0px -64px 0px' },
    )
    io.observe(el)
    return () => {
      io.disconnect()
      window.clearTimeout(fallbackTimer)
      window.clearInterval(fallbackTimer)
      if (delayTimer !== undefined) window.clearTimeout(delayTimer)
    }
  }, [delay, variant])

  return (
    <div
      ref={ref}
      className={`${variant === 'slideup' ? 'reveal-slideup' : 'reveal-target'} ${shown ? 'in-view' : ''} ${className}`.trim()}
    >
      {children}
    </div>
  )
}
